import { Component } from 'react';
import { Prompt } from 'react-router-dom';
import Immutable from 'immutable';
import { InlineAlert } from 'edrolo-style-guide';
import WistiaUtils from 'edrolo/utils/WistiaUtils';
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import { EXAM_QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';
import PT from 'prop-types';
import QuestionSolution from '../../../QuestionSolution';
import AnnotatedImageChecklist from '../../../AnnotatedImageChecklist';
import GrokSelfReflection from '../../../GrokSelfReflection';
import questionTypePropInterface from '../../questionTypePropInterface';
import { SelfMarking } from '../SelfMarking';
import { WaffleFlag } from 'edrolo/utils/Waffle';

class ReflectionView extends Component {
  get checklistItems() {
    return this.props.response.getIn(
      ['solution', 'exemplar_response_checklist_items'],
      Immutable.List()
    );
  }

  get checklistReflections() {
    return this.props.response.get('checklist_reflections', Immutable.List());
  }

  get isChecklistComplete() {
    return Immutable.is(
      this.checklistItems.map((item) => item.get('id')).toSet(),
      this.checklistReflections
        .map((item) => item.get('exemplar_response_checklist_item'))
        .toSet()
    );
  }

  get isSelfReflectionComplete() {
    return this.props.response?.get('grokked') !== QUESTION_GROKKED.NO_RESPONSE;
  }

  onClickChecklistItem = (checklistItemId, checked, currentChecked) => {
    const { onSubmitChecklistReflectionItem } = this.props;
    onSubmitChecklistReflectionItem({
      exemplar_response_checklist_item: checklistItemId,
      checked,
      currentChecked,
    });
  };

  getNavigationWarning = () => {
    if (
      !this.isSelfReflectionComplete &&
      this.props.showIncompleteChecklistWarning
    ) {
      return 'Your response to the checklist and self-reflection is incomplete.\nAre you sure you want to leave this question?';
    } else if (
      !this.isChecklistComplete &&
      !this.props.showIncompleteChecklistWarning
    ) {
      return 'Your response to the checklist is incomplete.\nAre you sure you want to leave this question?';
    } else {
      return null;
    }
  };

  render() {
    const {
      question,
      response,
      onSubmitGrok,
      onSubmitMark,
      grokkingItemIsSaving,
      checklistItemIsSaving,
      showIncompleteChecklistWarning,
    } = this.props;

    const message = this.getNavigationWarning();

    return (
      <div data-test={`answer-${EXAM_QUESTION_TYPES.WORKED_ANNOTATED_IMAGE}`}>
        {message && <Prompt message={message} />}

        <QuestionSolution
          headingText="Solution"
          questionId={question.get('id')}
          explanation={response.getIn(['solution', 'explanation'])}
          videoHash={response.getIn(['solution', 'video', 'wistia_hashed_id'])}
          videoThumbnailUrl={WistiaUtils.getThumbnail(
            response.getIn(['solution', 'video'])
          )}
        />
        {showIncompleteChecklistWarning ? (
          <InlineAlert.Warning>
            Your response to the checklist is incomplete
          </InlineAlert.Warning>
        ) : null}
        <AnnotatedImageChecklist
          checklistItems={this.checklistItems}
          checklistReflections={this.checklistReflections}
          imageUrl={response.getIn(['solution', 'annotation_image_url'])}
          checklistImageAnnotations={response.getIn(
            ['solution', 'checklist_image_annotations'],
            Immutable.Map()
          )}
          onChecklistReflection={this.onClickChecklistItem}
          isSaving={checklistItemIsSaving}
        />
        <WaffleFlag name={'questions-self-marking'}>
          <SelfMarking
            onChange={onSubmitMark}
            mark={response.get('text_marks_from_student')}
            totalMarks={Number(question.get('marks'))}
          />
        </WaffleFlag>
        <GrokSelfReflection
          value={response.get('grokked')}
          onChange={onSubmitGrok}
          isSaving={grokkingItemIsSaving}
        />
      </div>
    );
  }
}

ReflectionView.propTypes = {
  ...questionTypePropInterface,
  onSubmitChecklistReflectionItem: PT.func,
};

export default ReflectionView;
